/**
 * ActiveLiveSiteEventsCard.tsx
 * ----------------
 * Card displaying a table containing details about already resolved live site events.
 */

import * as React from 'react';
import { Link } from 'react-router-dom';

import { LiveSiteEventSeverity } from '../../models/status';
import { LiveSiteEventItem } from '../../models/ui/liveSiteEvent';
import { StatusIndicator } from '../shared/StatusIndicator';
import { Table } from './Table';
import { compose } from '../../utilities/compose';
import { getTitleForGeography, isAllGeographiesCollection } from '../../utilities/geographies';
import { getDisplayNameForService } from '../../utilities/services';
import {
    formatUtcAsLocalInEnglishLocale, formatDateComponentAsWeekdayAndDateFormat, getUtcDateFromUtcTimestamp,
    getLocalDateComponentFromUtcTimestamp,
} from '../../utilities/time';

/**
 * Component interfaces
 */

interface ResolvedLiveSiteEventsCardProps {
    liveSiteEvents: LiveSiteEventItem[];
    liveSiteEventUrl: (id: number) => string;
}

/**
 * Private functions
 */

function caseInsensitiveStringComparer (a: string, b: string) {
    if (a.toLowerCase() < b.toLowerCase()) {
        return -1;
    }

    if (a.toLowerCase() > b.toLowerCase()) {
        return 1;
    }

    return 0;
}

function dateComparer (a: Date, b: Date) {
    if (a < b) {
        return -1;
    }

    if (a > b) {
        return 1;
    }

    return 0;
}

function serviceComparer (a: string, b: string) {
    const aDisplayName = getDisplayNameForService(a);
    const bDisplayName = getDisplayNameForService(b);

    if (aDisplayName < bDisplayName) {
        return -1;
    }

    if (aDisplayName > bDisplayName) {
        return 1;
    }

    return 0;
}

function severityComparer (a: LiveSiteEventSeverity, b: LiveSiteEventSeverity) {
    if (a === 'Unhealthy' && b !== 'Unhealthy') {
        return -1;
    }

    if (a === 'Degraded' && b === 'Unhealthy') {
        return 1;
    }

    if (a === 'Degraded' && b === 'Advisory') {
        return -1;
    }

    if (a === 'Advisory' && b !== 'Advisory') {
        return 1;
    }

    return 0;
}

/**
 * Card displaying a table containing details about already resolved live site events.
 * @param props Component properties
 * @returns Rendered content
 */
export function ResolvedLiveSiteEventsCard (props: ResolvedLiveSiteEventsCardProps) {
    const { liveSiteEvents, liveSiteEventUrl } = props;
    const timestampGroup = compose(getLocalDateComponentFromUtcTimestamp, formatDateComponentAsWeekdayAndDateFormat);

    // Validation
    liveSiteEvents.forEach(liveSiteEvent => {
        if (liveSiteEvent.resolvedTimestamp === undefined) {
            console.warn(`Live site event #${liveSiteEvent.id} is in resolved list, but has no resolution timestamp.`);
        }
    });

    return (
        <div className="resolved-live-site-events-card ms-depth-8">
            <Table
                columns={[
                    {
                        cellClassName: 'resolved-live-site-events-card__severity resolved-live-site-events-card__cell',
                        compareTo: (a, b) => severityComparer(a.severity, b.severity),
                        group: liveSiteEvent => liveSiteEvent.severity,
                        groupCompareTo: severityComparer,
                        groupTitle: (severity: LiveSiteEventSeverity) => severity,
                        headerClassName: 'resolved-live-site-events-card__severity resolved-live-site-events-card__severity--header',
                        hideOnReflow: true,
                        /* eslint-disable react/display-name */
                        // Justification: this isn't really a component.
                        render: liveSiteEvent => <StatusIndicator
                            severity={liveSiteEvent.severity}
                            state="Active"
                            svgStyles={{ height: 20, width: 20 }} />,
                        /* eslint-enable react/display-name */
                        sortMode: 'grouped',
                        title: 'Sev',
                    },
                    {
                        cellClassName: 'resolved-live-site-events-card__title resolved-live-site-events-card__cell',
                        compareTo: (a, b) => caseInsensitiveStringComparer(a.title, b.title),
                        headerClassName: 'resolved-live-site-events-card__title resolved-live-site-events-card__title--header',
                        hideOnReflow: true,
                        /* eslint-disable react/display-name */
                        // Justification: this isn't really a component.
                        render: liveSiteEvent =>
                            <Link
                                className="resolved-live-site-events-card__link ms-Link"
                                to={liveSiteEventUrl(liveSiteEvent.id)}>
                                {liveSiteEvent.title}
                            </Link>,
                        /* eslint-enable react/display-name */
                        sortMode: 'ungrouped',
                        title: 'Title',
                    },
                    {
                        cellClassName: 'resolved-live-site-events-card__service resolved-live-site-events-card__cell',
                        compareTo: (a, b) => serviceComparer(a.service, b.service),
                        group: liveSiteEvent => getDisplayNameForService(liveSiteEvent.service),
                        groupCompareTo: caseInsensitiveStringComparer,
                        groupTitle: (serviceDisplayName: string) => serviceDisplayName,
                        headerClassName: 'resolved-live-site-events-card__service resolved-live-site-events-card__service--header',
                        /* eslint-disable react/display-name */
                        // Justification: this isn't really a component.
                        render: liveSiteEvent => <span>{getDisplayNameForService(liveSiteEvent.service)}</span>,
                        /* eslint-enable react/display-name */
                        sortMode: 'grouped',
                        title: 'Service',
                    },
                    {
                        cellClassName: 'resolved-live-site-events-card__start-time resolved-live-site-events-card__cell',
                        compareTo: (a, b) =>
                            dateComparer(getUtcDateFromUtcTimestamp(a.impactStartTimestamp), getUtcDateFromUtcTimestamp(b.impactStartTimestamp)),
                        group: liveSiteEvent => timestampGroup(liveSiteEvent.impactStartTimestamp),
                        groupCompareTo: (a: string, b: string) => dateComparer(new Date(a), new Date(b)),
                        groupTitle: (dateString: string) => dateString,
                        headerClassName: 'resolved-live-site-events-card__start-time resolved-live-site-events-card__start-time--header',
                        /* eslint-disable react/display-name */
                        // Justification: this isn't really a component.
                        render: liveSiteEvent => <span>{formatUtcAsLocalInEnglishLocale(liveSiteEvent.impactStartTimestamp)}</span>,
                        /* eslint-enable react/display-name */
                        sortMode: 'grouped',
                        title: 'Start Time',
                    },
                    {
                        cellClassName: 'resolved-live-site-events-card__resolved-time resolved-live-site-events-card__cell',
                        compareTo: (a, b) =>
                            dateComparer(getUtcDateFromUtcTimestamp(a.resolvedTimestamp as string), getUtcDateFromUtcTimestamp(b.resolvedTimestamp as string)),
                        group: liveSiteEvent => timestampGroup(liveSiteEvent.resolvedTimestamp as string),
                        groupCompareTo: (a: string, b: string) => dateComparer(new Date(a), new Date(b)),
                        groupTitle: (dateString: string) => dateString,
                        headerClassName: 'resolved-live-site-events-card__resolved-time resolved-live-site-events-card__resolved-time--header',
                        /* eslint-disable react/display-name */
                        // Justification: this isn't really a component.
                        render: liveSiteEvent => <span>{formatUtcAsLocalInEnglishLocale(liveSiteEvent.resolvedTimestamp as string)}</span>,
                        /* eslint-enable react/display-name */
                        sortMode: 'grouped',
                        title: 'Resolved Time',
                    },
                    {
                        cellClassName: 'resolved-live-site-events-card__geographies resolved-live-site-events-card__cell',
                        headerClassName: 'resolved-live-site-events-card__geographies resolved-live-site-events-card__geographies--header',
                        /* eslint-disable react/display-name */
                        // Justification: this isn't really a component.
                        render: liveSiteEvent => {
                            const geographies = liveSiteEvent.impacts.map(impact => impact.geography);

                            if (isAllGeographiesCollection(geographies)) {
                                return <span>All geographies</span>;
                            }

                            return <span>{geographies.map(g => getTitleForGeography(g)).sort().join(', ')}</span>;
                        },
                        /* eslint-enable react/display-name */
                        sortable: false,
                        title: 'Geographies',
                    }
                ]}
                data={liveSiteEvents}
                name="Resolved live site events"
                reflowOptions={{
                    /* eslint-disable react/display-name */
                    // Justification: this isn't really a component.
                    renderSideSection: liveSiteEvent => <StatusIndicator
                        severity={liveSiteEvent.severity}
                        state="Active"
                        svgStyles={{ height: 20, width: 20 }} />,
                    renderTitle: liveSiteEvent =>
                        <Link
                            className="active-live-site-events-card__link ms-Link"
                            to={liveSiteEventUrl(liveSiteEvent.id)}>
                            {liveSiteEvent.title}
                        </Link>,
                    /* eslint-enable react/display-name */
                }} />
        </div>
    );
}
